import {
  Button,
  DraggableWindowHeader,
  Icon,
  RadioWithLabel,
} from "@hoylu/client-common";
import { clsx } from "clsx";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import type { AppState } from "../../state/root.reducer";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  selectDashboardWorkspaceDetailsById,
  selectSelectedWorkspace,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { Modal } from "./Modal";
import Styles from "./RemoveWorkspaceDialog.module.css";

enum ActionType {
  HIDE = "HIDE",
  LEAVE = "LEAVE",
  DELETE = "DELETE",
}

export const RemoveWorkspaceDialog = () => {
  const dispatch = useDispatch();

  const t = useI18n("INFO_SECTION_REMOVE_WORKSPACE_DIALOG.");
  const tClose = useI18n("CLOSE_BUTTON.");

  const selectedWorkspace = useSelector(selectSelectedWorkspace);
  const selectedDashboardWorkspaceDetails = useSelector((state: AppState) =>
    selectDashboardWorkspaceDetailsById(state, selectedWorkspace?.workspaceId),
  );

  const canLeaveWorkspace = !!selectedWorkspace?.myPermissions.canLeave;
  const canHideWorkspace =
    !!selectedDashboardWorkspaceDetails?.isFromMyWorkspaces &&
    !selectedWorkspace?.isHidden;

  const [actionType, setActionType] = useState<ActionType>(() => {
    if (canHideWorkspace) return ActionType.HIDE;
    if (canLeaveWorkspace) return ActionType.LEAVE;
    return ActionType.DELETE;
  });

  const isDeleteAction = actionType === ActionType.DELETE;
  const iconStyles = {
    padding: "0",
    fontSize: "var(--global--size-h1)",
    color: isDeleteAction
      ? "var(--global--text--Color)"
      : "var(--global--dark-primary--Color)",
    marginRight: "var(--global--spacing--medium)",
  };
  const buttonStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const onClose = () => {
    dispatch(
      workspacesActions.activateDashboardOption({
        optionType: DashboardOption.INFO,
        workspaceId: selectedWorkspace?.workspaceId,
      }),
    );
  };

  const onSubmit = () => {
    switch (actionType) {
      case ActionType.HIDE:
        dispatch(
          workspacesActions.activateDashboardOption({
            optionType: DashboardOption.HIDE,
            workspaceId: selectedWorkspace?.workspaceId,
          }),
        );
        break;
      case ActionType.LEAVE:
        dispatch(
          workspacesActions.activateDashboardOption({
            optionType: DashboardOption.LEAVE,
            workspaceId: selectedWorkspace?.workspaceId,
          }),
        );
        break;
      case ActionType.DELETE:
        dispatch(
          workspacesActions.activateDashboardOption({
            optionType: DashboardOption.DELETE,
            workspaceId: selectedWorkspace?.workspaceId,
          }),
        );
        break;
      default:
        return;
    }
  };

  const renderInputs = () => {
    const inputs = [];

    for (const action in ActionType) {
      /*
        Skip "hide" option for hidden workspaces.
        Skip "leave" option for the last admin.
        Skip "delete" option for non admin.
       */
      if (
        (action === ActionType.HIDE && !canHideWorkspace) ||
        (action === ActionType.LEAVE && !canLeaveWorkspace) ||
        (action === ActionType.DELETE &&
          !selectedWorkspace?.myPermissions.canAdmin)
      ) {
        continue;
      }

      const identity = `${action.toLowerCase()}-workspace`;

      const input = (
        <RadioWithLabel
          checked={actionType === action}
          handleChange={() =>
            setActionType(ActionType[action as keyof typeof ActionType])
          }
          id={identity}
          key={identity}
          label={t(`${action}_WORKSPACE`)}
          value={identity}
        />
      );

      inputs.push(input);
    }

    return inputs;
  };

  const mapButtonIcon = useMemo(() => {
    switch (actionType) {
      case ActionType.HIDE:
        return "hoylu-ui-icons-indicator-hide";
      case ActionType.LEAVE:
        return "hoylu-ui-icons-leave";
      case ActionType.DELETE:
        return "hoylu-ui-icons-trash";
      default:
        return "";
    }
  }, [actionType]);

  return (
    <Modal
      allowContentToOverflow
      containerClassName={Styles.front}
      handleOutsideClick={onClose}
    >
      <div
        className={clsx(Styles.front, Styles.removeWorkspaceDialog)}
        data-test-id="remove-ws-info-section"
      >
        <DraggableWindowHeader
          close={tClose("CLOSE")}
          onClose={onClose}
          title={t("TITLE")}
        />
        <div className={Styles.inputs}>{renderInputs()}</div>
        <div className={Styles.buttons}>
          <Button dataTestId={t("CANCEL")} onClick={onClose} type="secondary">
            {t("CANCEL")}
          </Button>
          <Button
            additionalStyles={buttonStyles}
            dataTestId={t(actionType)}
            onClick={onSubmit}
            type={isDeleteAction ? "attention" : "primary"}
          >
            <Icon
              hoverStyle={{ backgroundColor: "none", color: "unset" }}
              icon={mapButtonIcon}
              style={iconStyles}
            />
            {t(actionType)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RemoveWorkspaceDialog;
